import React, { useContext, useEffect } from 'react';
import { Container } from '@amzn/awsui-components-react/polaris';
import { useLocation } from 'react-router-dom';
import 'src/components/CustomAppLayout.scss';
import 'src/components/custom-tables-shared/CustomTables.scss';
import ServiceCollection from 'src/services/ServiceCollection';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { GLOBAL_ACTIONS } from 'src/services/global/GlobalReducer';
import { GlobalAppContext } from 'src/components/App';
import DynamicBreadcrumb from 'src/models/navigation/DynamicBreadcrumb';
import { segmentTablesReducer } from 'src/services/segment-cdt/SegmentTablesReducer';
import { initialState } from 'src/services/segment-cdt/SegmentTablesState';
import { SegmentTablesProvider } from 'src/components/segment-cdt/SegmentTablesContext';

/**
 * Shared shell for the Segment Data Tables feature. Owns the cross-cutting chrome the list tab and the
 * detail data-viewer both need: its own `SegmentTablesContext` (a fresh reducer instance per mount),
 * the dynamic-breadcrumb wiring, and the page `Container`. The header + body are supplied by the caller
 * as `children` (rendered inside the Container), so each surface computes its own header from the data
 * it fetches — see `SegmentPageHeader` for the standard header row.
 *
 * Intentional divergence from the `CalculationBuilderView` template: that view declares its provider
 * inline in the view file, whereas this feature has TWO views (list + detail) that need the identical
 * provider + breadcrumb + container. Factoring them here keeps that setup in one place instead of
 * duplicating it across `SegmentTablesView` and `SegmentTableDetailView`.
 */
export default function SegmentFeatureShell(props: {
    services: ServiceCollection;
    breadcrumb: { label: string; title: string; description: string };
    children: React.ReactNode;
}) {
    const { services, breadcrumb, children } = props;
    const { globalDispatch } = useContext(GlobalAppContext);
    const [state, dispatch] = useReducerWithLogger(segmentTablesReducer, initialState);
    const { pathname } = useLocation();

    useEffect(() => {
        globalDispatch(GLOBAL_ACTIONS.SET_DYNAMIC_BREADCRUMB.withPayload(new DynamicBreadcrumb(
            pathname,
            breadcrumb.label,
            breadcrumb.title,
            breadcrumb.description,
        )));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, breadcrumb.label, breadcrumb.title, breadcrumb.description]);

    return (
        <SegmentTablesProvider services={services} state={state} dispatch={dispatch}>
            <Container data-class="containerWithHeaderAndGrid">
                {children}
            </Container>
        </SegmentTablesProvider>
    );
}
